import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Eye, EyeOff } from 'lucide-react';
import {
  credentialCreateSchema,
  type CredentialCreateInput,
  type CustomerRow,
  type PaginatedResponse,
} from '@runik/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Spinner } from '../ui/spinner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from '../ui/sonner';
import { ApiError, request } from '../../lib/auth/client';
import { createCredential } from '../../lib/queries/credentials';

type ProviderSlug = 'openai' | 'replicate';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CredentialFormDialog({ open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [serverError, setServerError] = useState<string | null>(null);
  const [showKey, setShowKey] = useState(false);

  const [customerId, setCustomerId] = useState('');
  const [providerSlug, setProviderSlug] = useState<ProviderSlug | ''>('');
  const [label, setLabel] = useState('');
  const [projectId, setProjectId] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [usageMultiplier, setUsageMultiplier] = useState('1');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const customers = useQuery({
    queryKey: ['customers', 'all-for-select'],
    queryFn: () => request<PaginatedResponse<CustomerRow>>('/api/customers?limit=100'),
    enabled: open,
  });

  useEffect(() => {
    if (open) {
      setServerError(null);
      setShowKey(false);
      setCustomerId('');
      setProviderSlug('');
      setLabel('');
      setProjectId('');
      setApiKey('');
      setUsageMultiplier('1');
      setFieldErrors({});
    }
  }, [open]);

  const create = useMutation({
    mutationFn: (data: CredentialCreateInput) => createCredential(data),
    onSuccess: (resp) => {
      toast.success(`Kimlik bilgisi oluşturuldu (${resp.identifier})`);
      qc.invalidateQueries({ queryKey: ['credentials'] });
      onOpenChange(false);
    },
  });

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);
    setFieldErrors({});

    if (!providerSlug) {
      setFieldErrors({ providerSlug: 'Sağlayıcı seçin' });
      return;
    }

    const multiplierVal = parseFloat(usageMultiplier);
    const payload: CredentialCreateInput =
      providerSlug === 'openai'
        ? { customerId, providerSlug: 'openai', projectId: projectId.trim(), label, usageMultiplier: isNaN(multiplierVal) ? 1 : multiplierVal }
        : { customerId, providerSlug: 'replicate', apiKey, label, usageMultiplier: isNaN(multiplierVal) ? 1 : multiplierVal };

    const parsed = credentialCreateSchema.safeParse(payload);
    if (!parsed.success) {
      const errs: Record<string, string> = {};
      for (const [k, v] of Object.entries(parsed.error.flatten().fieldErrors)) {
        if (v && v.length > 0) errs[k] = v[0]!;
      }
      setFieldErrors(errs);
      return;
    }

    try {
      await create.mutateAsync(parsed.data);
    } catch (err) {
      setServerError(err instanceof ApiError ? err.problem.detail ?? err.problem.title : 'Hata oluştu');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Yeni Kimlik Bilgisi</DialogTitle>
          <DialogDescription>
            OpenAI için müşteri proje ID'si (proj_…), Replicate için API token girilir.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          {serverError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{serverError}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label>Müşteri *</Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger>
                <SelectValue placeholder="Müşteri seçin" />
              </SelectTrigger>
              <SelectContent>
                {(customers.data?.data ?? []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.companyName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fieldErrors.customerId && <p className="text-sm text-rose-600">{fieldErrors.customerId}</p>}
          </div>

          <div className="space-y-2">
            <Label>Sağlayıcı *</Label>
            <Select
              value={providerSlug}
              onValueChange={(v) => setProviderSlug(v as ProviderSlug)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sağlayıcı seçin" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="openai">OpenAI</SelectItem>
                <SelectItem value="replicate">Replicate</SelectItem>
              </SelectContent>
            </Select>
            {fieldErrors.providerSlug && <p className="text-sm text-rose-600">{fieldErrors.providerSlug}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="label">Etiket *</Label>
            <Input
              id="label"
              placeholder="Acme Production, Test Project, vs"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
            {fieldErrors.label && <p className="text-sm text-rose-600">{fieldErrors.label}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="usageMultiplier">Kullanım Katsayısı</Label>
            <Input
              id="usageMultiplier"
              type="number"
              step="0.01"
              min="0.01"
              max="100"
              value={usageMultiplier}
              onChange={(e) => setUsageMultiplier(e.target.value)}
            />
            {fieldErrors.usageMultiplier && <p className="text-sm text-rose-600">{fieldErrors.usageMultiplier}</p>}
            <p className="text-xs text-slate-500">Varsayılan: 1.00 — maliyet bu katsayıyla çarpılarak gösterilir.</p>
          </div>

          {providerSlug === 'openai' && (
            <div className="space-y-2">
              <Label htmlFor="projectId">OpenAI Project ID *</Label>
              <Input
                id="projectId"
                placeholder="proj_abc123…"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                autoComplete="off"
              />
              <p className="text-xs text-slate-500">
                OpenAI panelinden → Projects → ilgili proje → Settings yolundan kopyalayın.
                Platform admin anahtarı, bu proje filtresiyle kullanımı çeker.
              </p>
              {fieldErrors.projectId && <p className="text-sm text-rose-600">{fieldErrors.projectId}</p>}
            </div>
          )}

          {providerSlug === 'replicate' && (
            <div className="space-y-2">
              <Label htmlFor="apiKey">Replicate API Token *</Label>
              <div className="relative">
                <Input
                  id="apiKey"
                  type={showKey ? 'text' : 'password'}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  autoComplete="off"
                />
                <button
                  type="button"
                  aria-label={showKey ? 'Gizle' : 'Göster'}
                  onClick={() => setShowKey((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
                >
                  {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {fieldErrors.apiKey && <p className="text-sm text-rose-600">{fieldErrors.apiKey}</p>}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              İptal
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? (
                <>
                  <Spinner /> Kaydediliyor…
                </>
              ) : (
                'Oluştur'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
